'use client';

import { Button } from '@components/button';
import { SmallCard, SmallCardProps } from '@components/card';
import { Typography } from '@components/typography';
import { PropsWithChildren, useCallback, useRef } from 'react';
import {
  Swiper as SwiperComponent,
  SwiperRef,
  SwiperSlide,
} from 'swiper/react';

import { useResize } from '@/shared/hooks/useResize';
import { ArrowRight24 } from '@/shared/icons/24/ArrowRight24';

export interface FeatureCardsCarouselItem extends SmallCardProps {
  id: string | number;
}

export type FeatureCardsCarouselVariant = 'sm' | 'lg';

interface CarouselItem {
  id: string | number;
}

export type FeatureCardsCarouselProps<
  T extends CarouselItem = FeatureCardsCarouselItem,
> = {
  title?: string;
  navButtonsPlacement?: 'title' | 'content' | 'bottom';
  navButtonsType?: 'round' | 'square';
  /** Дробное значение показывает край следующего слайда на мобилке */
  mobileSlidesPerView?: number;
  ContentWrapper?: React.FC<PropsWithChildren>;
} & (
  | {
      items: FeatureCardsCarouselItem[];
      renderCard?: never;
    }
  | {
      items: T[];
      /** Пользовательская карточка вместо SmallCard. Передаётся из клиентского компонента. */
      renderCard: (
        item: T,
        variant: FeatureCardsCarouselVariant,
      ) => React.ReactNode;
    }
);

export const FeatureCardsCarousel = <
  T extends CarouselItem = FeatureCardsCarouselItem,
>({
  title,
  items,
  ContentWrapper,
  renderCard,
  navButtonsPlacement = 'title',
  navButtonsType = 'round',
  mobileSlidesPerView = 1,
}: FeatureCardsCarouselProps<T>) => {
  const sliderRef = useRef<SwiperRef>(null);

  const { isScreenSm } = useResize();

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  const buttonStyles =
    navButtonsType === 'round' ? { borderRadius: '50%' } : { borderRadius: 12 };

  return (
    <>
      <div className="flex items-start justify-between gap-4 px-4">
        {title && (
          <Typography
            variant="heading-l"
            className="mb-12 max-sm:mb-9 md:max-w-[841px]"
          >
            {title}
          </Typography>
        )}

        {navButtonsPlacement === 'title' && (
          <div className="flex justify-center gap-3 max-sm:hidden">
            <Button
              style={buttonStyles}
              className="group text-text-base-tertiary hover:text-text-base-primary px-4 py-4"
              variant="primary"
              onClick={handlePrev}
              aria-label="previous slide"
            >
              <ArrowRight24 />
            </Button>

            <Button
              style={buttonStyles}
              className="group text-text-base-tertiary hover:text-text-base-primary px-4 py-4"
              variant="primary"
              onClick={handleNext}
              aria-label="next slide"
            >
              <ArrowRight24
                className="rotate-180"
                svgClassName="[&>path]:transition-all group-hover:[&>path]:fill-opacity-100"
              />
            </Button>
          </div>
        )}
      </div>
      <div className="relative w-full px-4">
        {navButtonsPlacement === 'content' && (
          <>
            <Button
              style={buttonStyles}
              className="group text-text-base-tertiary hover:text-text-base-primary absolute top-[50%] left-[-16px] z-2 translate-y-[-50%] px-4 py-4 max-sm:hidden"
              variant="primary"
              onClick={handlePrev}
              aria-label="previous slide"
            >
              <ArrowRight24 />
            </Button>

            <Button
              style={buttonStyles}
              className="group text-text-base-tertiary hover:text-text-base-primary absolute top-[50%] right-[-16px] z-2 translate-y-[-50%] px-4 py-4 max-sm:hidden"
              variant="primary"
              onClick={handleNext}
              aria-label="next slide"
            >
              <ArrowRight24
                className="rotate-180"
                svgClassName="[&>path]:transition-all group-hover:[&>path]:fill-opacity-100"
              />
            </Button>
          </>
        )}
        {ContentWrapper ? (
          <ContentWrapper>
            {renderCard
              ? items.map((item) => renderCard(item, isScreenSm ? 'sm' : 'lg'))
              : items.map((item) => <SmallCard {...item} />)}
          </ContentWrapper>
        ) : (
          <SwiperComponent
            ref={sliderRef}
            loop={!isScreenSm}
            spaceBetween={isScreenSm && mobileSlidesPerView > 1 ? 12 : 32}
            slidesPerView={isScreenSm ? mobileSlidesPerView : 3}
            slidesPerGroup={1}
          >
            {renderCard
              ? items.map((item) => (
                  <SwiperSlide key={item.id}>
                    {renderCard(item, isScreenSm ? 'sm' : 'lg')}
                  </SwiperSlide>
                ))
              : items.map((item) => (
                  <SwiperSlide key={item.id}>
                    <SmallCard {...item} />
                  </SwiperSlide>
                ))}
          </SwiperComponent>
        )}
      </div>

      {navButtonsPlacement === 'bottom' && (
        <div className="mt-6 flex justify-center gap-3 max-sm:hidden">
          <Button
            style={buttonStyles}
            className="group text-text-base-tertiary hover:text-text-base-primary px-4 py-4"
            variant="primary"
            onClick={handlePrev}
            aria-label="previous slide"
          >
            <ArrowRight24 />
          </Button>

          <Button
            style={buttonStyles}
            className="group text-text-base-tertiary hover:text-text-base-primary px-4 py-4"
            variant="primary"
            onClick={handleNext}
            aria-label="next slide"
          >
            <ArrowRight24
              className="rotate-180"
              svgClassName="[&>path]:transition-all group-hover:[&>path]:fill-opacity-100"
            />
          </Button>
        </div>
      )}
    </>
  );
};
